import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { X, Upload, Trash2, Shield, ToggleLeft, ToggleRight, User, Mail, Phone, FileText, Pencil, Eye, EyeOff, ArrowUpDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditMemberModal = ({ member, getAuthHeaders, onClose, onRefresh }) => {
  const [editForm, setEditForm] = useState({
    member_name: member.member_name || "",
    nickname: member.nickname || "",
    email: member.email || "",
    bio: member.bio || "",
    phone: member.phone || "",
    phone_private: member.phone_private || false,
    title: member.title || "",
    show_public: member.show_public !== false,
    display_order: member.display_order ?? "",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImageUploading, setEditImageUploading] = useState(false);
  const [memberState, setMemberState] = useState(member);

  const saveEditMember = async () => {
    setEditSaving(true);
    try {
      const payload = { ...editForm, display_order: editForm.display_order === "" ? null : editForm.display_order };
      await axios.put(`${API}/member-codes/${encodeURIComponent(member.member_name)}/admin-edit`, payload, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } });
      if (editImageFile) {
        setEditImageUploading(true);
        const form = new FormData();
        form.append("file", editImageFile);
        await axios.post(`${API}/member-codes/${encodeURIComponent(editForm.member_name || member.member_name)}/profile-image`, form, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
        setEditImageUploading(false);
      }
      toast.success("Profil opdateret");
      setEditSaved(true);
      setTimeout(() => { onClose(); onRefresh(); }, 1200);
    } catch (err) {
      setEditImageUploading(false);
      toast.error(err.response?.data?.detail || "Kunne ikke opdatere");
    } finally {
      setEditSaving(false);
    }
  };

  const changeRole = async (memberName, newRole) => {
    try {
      const res = await axios.put(`${API}/member-codes/${encodeURIComponent(memberName)}/role`, { role: newRole }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke ændre rolle");
    }
  };

  const toggleMember = async (memberName) => {
    try {
      const res = await axios.post(`${API}/member-codes/${encodeURIComponent(memberName)}/toggle`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setMemberState(prev => ({ ...prev, is_active: prev.is_active === false ? true : false }));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl");
    }
  };

  const deleteMember = async (memberName) => {
    if (!window.confirm(`Er du sikker på du vil slette ${memberName}?`)) return;
    try {
      await axios.delete(`${API}/member-codes/${encodeURIComponent(memberName)}`, { headers: getAuthHeaders() });
      toast.success(`${memberName} er slettet`);
      onClose();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-member-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ background: "linear-gradient(135deg, #0f0f12 0%, #09090b 100%)", border: "1px solid rgba(0,212,255,0.2)", boxShadow: "0 0 40px rgba(0,0,0,0.8)" }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-['Oswald'] text-white tracking-wide">MEDLEMSPROFIL</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
          </div>

          {/* Profile image + upload */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 flex items-center justify-center overflow-hidden mb-3" style={{ boxShadow: "0 0 20px rgba(0,0,0,0.5)" }}>
              {editImageFile ? (
                <img src={URL.createObjectURL(editImageFile)} alt="" className="w-full h-full object-cover" />
              ) : member.profile_image ? (
                <img src={member.profile_image.startsWith("http") ? member.profile_image : `${process.env.REACT_APP_BACKEND_URL}${member.profile_image}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={40} className="text-gray-500" />
              )}
            </div>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400 hover:text-[#00d4ff] hover:bg-[#00d4ff]/5 rounded cursor-pointer transition-all border border-gray-800 hover:border-[#00d4ff]/30" data-testid="edit-member-upload-img">
                <Upload size={14} /> Upload billede
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && setEditImageFile(e.target.files[0])} />
              </label>
              {(editImageFile || member.profile_image) && (
                <button onClick={() => setEditImageFile(null)} className="px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 rounded border border-gray-800 transition-all">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Member info */}
          <div className="text-center mb-6">
            <p className="text-gray-600 text-xs">
              Kode: <span className="font-mono text-gray-400">{member.code === "__ADMIN__" ? "\u2014" : member.code}</span>
              {member.last_login && <> · Sidst set: {new Date(member.last_login).toLocaleDateString("da-DK")}</>}
              {member.login_count && <> · {member.login_count} logins</>}
              {member.created_at && <> · Medlem siden: {new Date(member.created_at).toLocaleDateString("da-DK")}</>}
            </p>
          </div>

          {/* Role & Status controls */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><Shield size={12} /> Gruppe</label>
              <select
                data-testid="edit-member-role"
                className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none"
                value={editForm.role || member.role || "member"}
                onChange={(e) => {
                  setEditForm(p => ({ ...p, role: e.target.value }));
                  changeRole(member.member_name, e.target.value);
                }}
              >
                <option value="admin">Gruppe 1 (Admin)</option>
                <option value="editor">Gruppe 2 (Redaktør)</option>
                <option value="member">Gruppe 3 (Medlem)</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><ToggleRight size={12} /> Status</label>
              <button
                data-testid="edit-member-toggle"
                onClick={() => toggleMember(member.member_name)}
                className={`w-full px-3 py-2.5 text-sm rounded border transition-all flex items-center justify-center gap-2 ${memberState.is_active !== false ? 'bg-green-600/10 border-green-600/30 text-green-400' : 'bg-red-600/10 border-red-600/30 text-red-400'}`}
              >
                {memberState.is_active !== false ? <><ToggleRight size={16} /> Aktiv</> : <><ToggleLeft size={16} /> Deaktiveret</>}
              </button>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><Eye size={12} /> Offentlig</label>
              <button
                data-testid="edit-member-show-public"
                onClick={() => setEditForm(p => ({ ...p, show_public: !p.show_public }))}
                className={`w-full px-3 py-2.5 text-sm rounded border transition-all flex items-center justify-center gap-2 ${editForm.show_public ? 'bg-emerald-600/10 border-emerald-600/30 text-emerald-400' : 'bg-yellow-600/10 border-yellow-600/30 text-yellow-400'}`}
              >
                {editForm.show_public ? <><Eye size={16} /> Synlig</> : <><EyeOff size={16} /> Skjult</>}
              </button>
            </div>
          </div>

          {/* Editable fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><User size={12} /> Navn</label>
                <input data-testid="edit-member-name" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.member_name} onChange={(e) => setEditForm(p => ({ ...p, member_name: e.target.value }))} />
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><User size={12} /> Klubnavn</label>
                <input data-testid="edit-member-nickname" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.nickname} onChange={(e) => setEditForm(p => ({ ...p, nickname: e.target.value }))} placeholder="F.eks. DD, Tansen, Gansen..." />
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><Shield size={12} /> Titel (vises offentligt)</label>
              <input data-testid="edit-member-title" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.title} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} placeholder="F.eks. Præsident, Road Captain, Medlem..." />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><ArrowUpDown size={12} /> Placering på offentlig liste</label>
              <input data-testid="edit-member-display-order" type="number" min="1" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.display_order} onChange={(e) => setEditForm(p => ({ ...p, display_order: e.target.value ? parseInt(e.target.value) : "" }))} placeholder="1 = først, 2 = næst osv." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><Mail size={12} /> Email</label>
                <input data-testid="edit-member-email" type="email" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} placeholder="email@eksempel.dk" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><Phone size={12} /> Telefon</label>
                <input data-testid="edit-member-phone" type="tel" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none" value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} placeholder="+45 12 34 56 78" />
                <button
                  type="button"
                  onClick={() => setEditForm(p => ({ ...p, phone_private: !p.phone_private }))}
                  className="flex items-center gap-2 mt-2"
                  data-testid="edit-member-phone-private-toggle"
                >
                  <div className={`relative w-8 h-[18px] rounded-full transition-colors duration-200 ${editForm.phone_private ? 'bg-green-500' : 'bg-gray-700'}`}>
                    <div className={`absolute top-[1px] w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${editForm.phone_private ? 'translate-x-[14px]' : 'translate-x-[1px]'}`}></div>
                  </div>
                  <span className={`text-[10px] ${editForm.phone_private ? 'text-green-400' : 'text-gray-500'}`}>
                    {editForm.phone_private ? "Kun internt" : "Offentlig"}
                  </span>
                </button>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5"><FileText size={12} /> Om mig</label>
              <textarea data-testid="edit-member-bio" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none resize-none" rows={3} maxLength={500} value={editForm.bio} onChange={(e) => setEditForm(p => ({ ...p, bio: e.target.value }))} placeholder="Beskriv medlemmet..." />
              <p className="text-right text-xs text-gray-600 mt-1">{editForm.bio.length}/500</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 mt-6">
            <button onClick={saveEditMember} disabled={editSaving || editSaved} className="flex-1 chrome-btn px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-edit-member-btn">
              {editSaving ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {editImageUploading ? "Uploader billede..." : "Gemmer..."}</> : editSaved ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Gemt!</> : <><Pencil size={14} /> Gem Ændringer</>}
            </button>
          </div>

          {/* Delete section */}
          {member.code !== "__ADMIN__" && (
            <div className="mt-6 pt-4 border-t border-gray-800/50">
              <button
                onClick={() => deleteMember(member.member_name)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded border border-red-900/30 transition-all"
                data-testid="edit-member-delete-btn"
              >
                <Trash2 size={14} /> Slet dette medlem permanent
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditMemberModal;
